import { useEffect, useState } from "react";

/**
 * ForecastLoader — a staged waiting animation that communicates what's
 * happening on the server so users don't feel like they're waiting on a
 * dumb spinner. Four stages, ~2.5s each, auto-cycled with a fade.
 *
 * Why this exists: the /api/manage/{token}/subscriptions/preview endpoint
 * takes 8-15s on a cold cache (6 weather models + AI narrative + HTML
 * render). A generic spinner signals "we're stuck". A staged walkthrough
 * with iconography feels purposeful and survivable.
 *
 * Stages use lucide-react icons already in the bundle — no new deps.
 */
import { Cloud, Layers3, Sparkles, FileText } from "lucide-react";

const STAGES = [
  { icon: Cloud, title: "Pulling 6 weather models", detail: "ECMWF, GFS, ICON, NAM, Open-Meteo, MET Norway" },
  { icon: Layers3, title: "Ensembling the data", detail: "Adaptive per-location weighting" },
  { icon: Sparkles, title: "Running AI analysis", detail: "Turning numbers into a plain-English forecast" },
  { icon: FileText, title: "Formatting your digest", detail: "Wrapping it up for your inbox and dashboard" },
];

export default function ForecastLoader({ label = "Building your forecast" }) {
  const [stage, setStage] = useState(0);
  useEffect(() => {
    const id = setInterval(() => {
      setStage((s) => (s + 1) % STAGES.length);
    }, 2400);
    return () => clearInterval(id);
  }, []);

  return (
    <div
      className="flex flex-col items-center justify-center py-16 px-6 text-center"
      data-testid="forecast-loader"
    >
      <div className="ticker-label mb-3 text-neutral-600">{label}</div>
      <div className="relative w-full max-w-md">
        {STAGES.map((s, i) => {
          const Icon = s.icon;
          const active = i === stage;
          return (
            <div
              key={i}
              className={`flex items-start gap-4 p-4 border-2 border-black transition-opacity duration-500 ${
                active ? "opacity-100 bg-[#FFFBE6]" : "opacity-40 bg-white"
              } ${i > 0 ? "-mt-[2px]" : ""}`}
              data-testid={`loader-stage-${i}`}
              data-active={active}
            >
              <div
                className={`flex items-center justify-center w-10 h-10 border-2 border-black shrink-0 ${
                  active ? "bg-black text-[#FFD600] animate-pulse" : "bg-white text-black"
                }`}
              >
                <Icon className="w-5 h-5" />
              </div>
              <div className="text-left flex-1 min-w-0">
                <div className="font-display text-sm font-black tracking-tight leading-tight">{s.title}</div>
                <div className="text-xs text-neutral-600 mt-0.5 leading-tight">{s.detail}</div>
              </div>
              {active && (
                <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#0A0A0A] self-center shrink-0">
                  …
                </div>
              )}
            </div>
          );
        })}
      </div>
      <p className="text-xs text-neutral-500 mt-5 max-w-xs">
        Only happens on a cold cache — next time your forecast loads instantly.
      </p>
    </div>
  );
}
